import axios from 'axios';
import { hosts, storageKeys } from '../config/environment';
import { getSession, refreshAccessToken, clearSession } from './oauth';

// Construire la baseURL comme dans le mobile : API_URL + prefix
// Pour les requêtes authentifiées : /api/v1
// Pour les requêtes publiques : /exposed/v1
const getBaseURL = (withAuth = false) => {
    const apiUrl = hosts.api || '';
    const prefix = withAuth ? '/api/v1' : '/exposed/v1';
    return apiUrl + prefix;
};

const baseURL = hosts.api || '';

/**
 * Récupère la session stockée
 */
const getStoredSession = () => {
    return getSession();
};

/**
 * Vérifie si le token est expiré ou proche de l'expiration
 */
const isTokenExpiringSoon = (session, thresholdSeconds = 60) => {
    if (!session?.expires_at) return true;
    const now = Date.now();
    const expiresAt = session.expires_at;
    const threshold = thresholdSeconds * 1000;
    return (expiresAt - now) < threshold;
};

/**
 * Crée un client Axios avec interceptors pour l'authentification
 */
const createClient = (withAuth = false) => {
    // Construire la baseURL comme dans le mobile
    const clientBaseURL = getBaseURL(withAuth);
    
    // Log pour déboguer la baseURL
    if (typeof window !== 'undefined') {
        console.log(`[apiClient] Création du client ${withAuth ? 'authentifié' : 'public'} avec baseURL:`, clientBaseURL);
    }
    
    const client = axios.create({
        baseURL: clientBaseURL,
        timeout: 30000 // 30 secondes
    });

    // Interceptor pour ajouter le token d'authentification
    client.interceptors.request.use(
        async (config) => {
            const headers = config.headers || {};
            headers['Accept'] = headers['Accept'] || 'application/json';
            
            // Ne pas définir Content-Type si c'est FormData
            if (!(config.data instanceof FormData)) {
                headers['Content-Type'] = headers['Content-Type'] || 'application/json';
            }

            if (withAuth) {
                let session = getStoredSession();
                
                // Si le token est expiré ou proche de l'expiration, essayer de le rafraîchir
                if (session && isTokenExpiringSoon(session)) {
                    if (session.refresh_token) {
                        try {
                            session = await refreshAccessToken(session.refresh_token);
                        } catch (error) {
                            console.error('[apiClient] Erreur lors du rafraîchissement du token', error);
                            // Si le refresh échoue, on continue avec le token actuel
                            // L'erreur sera gérée par l'interceptor de réponse
                        }
                    }
                }

                if (session?.access_token) {
                    headers['Authorization'] = `Bearer ${session.access_token}`;
                } else {
                    console.warn('[apiClient] Aucun token d\'accès trouvé dans la session');
                }
            }

            return {
                ...config,
                headers
            };
        },
        (error) => Promise.reject(error)
    );

    // Interceptor pour gérer les erreurs de réponse
    client.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            // Si l'erreur est 401 (Unauthorized) et qu'on n'a pas déjà tenté de rafraîchir
            if (error?.response?.status === 401 && !originalRequest._retry && withAuth) {
                originalRequest._retry = true;

                const session = getStoredSession();
                if (session?.refresh_token) {
                    try {
                        const newSession = await refreshAccessToken(session.refresh_token);
                        // Réessayer la requête avec le nouveau token
                        originalRequest.headers['Authorization'] = `Bearer ${newSession.access_token}`;
                        return client(originalRequest);
                    } catch (refreshError) {
                        // Si le refresh échoue, on déconnecte l'utilisateur
                        console.error('[apiClient] Impossible de rafraîchir le token', refreshError);
                        clearSession();
                        // Déclencher un événement pour notifier l'application
                        window.dispatchEvent(new CustomEvent('auth:logout'));
                        return Promise.reject(refreshError);
                    }
                }
            }

            // Pour les autres erreurs, formater le message
            const message =
                error?.response?.data?.message ||
                error?.response?.data?.error_description ||
                error?.response?.data?.error ||
                error?.message ||
                'Erreur réseau';
            
            const enhancedError = new Error(message);
            enhancedError.status = error?.response?.status;
            enhancedError.response = error?.response;
            enhancedError.config = error?.config;
            
            return Promise.reject(enhancedError);
        }
    );

    return client;
};

export const publicApiClient = createClient(false);
export const privateApiClient = createClient(true);

export const clearStoredSession = clearSession;

